/**
 * Global UI feedback: toasts, confirm dialog, loading bar. Plain reactive modules (no Pinia):
 * they hold no business state and are used from services outside components too.
 */
import { reactive, ref } from 'vue';
import {
  ApiError,
  ConflictError,
  ForbiddenError,
  LockedError,
  NetworkError,
  NotFoundError,
  PreconditionFailedError,
  RateLimitedError,
  ValidationError,
} from '../api/client';
import { t } from '../i18n';

// ---------------------------------------------------------------------------
// toasts
// ---------------------------------------------------------------------------

export type ToastKind = 'success' | 'info' | 'warning' | 'danger';
export interface ToastAction {
  label: string;
  run: () => void;
}
export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  title?: string;
  /** ms; 0 = sticky */
  timeout: number;
  action?: ToastAction;
}

export const toasts = reactive<Toast[]>([]);
let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export function dismissToast(id: number): void {
  const i = toasts.findIndex((t) => t.id === id);
  if (i >= 0) toasts.splice(i, 1);
  const tm = timers.get(id);
  if (tm) clearTimeout(tm);
  timers.delete(id);
}

export function toast(message: string, kind: ToastKind = 'info', opts: { title?: string; timeout?: number; action?: ToastAction; key?: string } = {}): number {
  // identical message already shown → do not stack
  const dup = toasts.find((t) => t.message === message && t.kind === kind);
  if (dup) return dup.id;
  const timeout = opts.timeout ?? (kind === 'danger' ? 8000 : 4000);
  const t: Toast = { id: nextId++, kind, message, timeout, ...(opts.title ? { title: opts.title } : {}), ...(opts.action ? { action: opts.action } : {}) };
  toasts.push(t);
  if (toasts.length > 5) dismissToast(toasts[0]!.id);
  if (timeout > 0) timers.set(t.id, setTimeout(() => dismissToast(t.id), timeout));
  return t.id;
}

/** User-facing message of an error (typed API errors → translated text). */
export function errorMessage(err: unknown): string {
  if (err instanceof NetworkError) return t('error.network');
  if (err instanceof RateLimitedError) return t('error.rateLimited');
  if (err instanceof LockedError) return err.lockedBy ? t('error.lockedBy', { user: err.lockedBy }) : t('error.locked');
  if (err instanceof PreconditionFailedError) return t('error.stale');
  if (err instanceof ValidationError) return err.message || t('error.validation');
  if (err instanceof ForbiddenError) return err.message || t('error.forbidden');
  if (err instanceof NotFoundError) return err.message || t('error.notFound');
  if (err instanceof ConflictError) return err.message || t('error.conflict');
  if (err instanceof ApiError) return err.status >= 500 ? t('error.server') : err.message || t('error.generic');
  return t('error.generic');
}

/** Show an error as toast (401 is handled by the router redirect and not shown). */
export function showError(err: unknown): void {
  if (err instanceof ApiError && err.status === 401) return;
  if ((err as Error)?.name === 'AbortError') return;
  if (!(err instanceof ApiError) && !(err instanceof NetworkError)) console.error(err);
  toast(errorMessage(err), 'danger');
}

// ---------------------------------------------------------------------------
// confirm dialog
// ---------------------------------------------------------------------------

export interface ConfirmRequest {
  title?: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** show a text input; the promise then resolves with the text (or null) */
  prompt?: { label: string; value?: string };
}
interface PendingConfirm extends ConfirmRequest {
  resolve: (v: boolean | string | null) => void;
}

export const confirmState = ref<PendingConfirm | null>(null);

function open<T extends boolean | string | null>(req: ConfirmRequest): Promise<T> {
  // a dialog already open is answered with "cancel"
  confirmState.value?.resolve(confirmState.value.prompt ? null : false);
  return new Promise<T>((resolve) => {
    confirmState.value = { ...req, resolve: (v) => resolve(v as T) };
  });
}

/** Ask for confirmation (resolves true/false). */
export const confirm = (req: ConfirmRequest | string): Promise<boolean> => open<boolean>(typeof req === 'string' ? { message: req } : req);

/** Ask for a text (resolves the text or null on cancel). */
export const prompt = (req: ConfirmRequest & { prompt: { label: string; value?: string } }): Promise<string | null> => open<string | null>(req);

export function answerConfirm(value: boolean | string | null): void {
  const c = confirmState.value;
  confirmState.value = null;
  c?.resolve(value);
}

// ---------------------------------------------------------------------------
// loading bar (API requests and lazy route chunks)
// ---------------------------------------------------------------------------

export const loading = reactive({ active: 0, visible: false });
let showTimer: ReturnType<typeof setTimeout> | undefined;

/** +1 when something starts, -1 when it ends; the bar appears after 150 ms. */
export function trackLoading(delta: 1 | -1): void {
  loading.active = Math.max(0, loading.active + delta);
  if (loading.active > 0 && !loading.visible && !showTimer) {
    showTimer = setTimeout(() => {
      showTimer = undefined;
      if (loading.active > 0) loading.visible = true;
    }, 150);
  }
  if (loading.active === 0) {
    if (showTimer) clearTimeout(showTimer);
    showTimer = undefined;
    loading.visible = false;
  }
}
